import React from 'react'
import { useParams } from 'react-router'
import styled from '@emotion/styled'
import { hexColors } from '@mobi/settings'
import { PlanSeq } from '@mobi/api-types'
import { colors, spacing } from '@mobi/component-library/Theme/Common'
import type { Tabs, Ticket } from '@core/Areas/AccountActivity/types'
import {
  formatDateTime,
  getTicketLayoutType,
  isTippingTicket,
} from '@core/Areas/AccountActivity/Components/Ticket/helpers'
import { DetailsSecondaryInfoStyled as SecondaryInfo } from './Details.styles'
import { TicketDisplayContext } from '@core/Areas/AccountActivity/context'
import {
  BettingInformationOverrides,
  FixedOddsMarketDisplayNames,
} from '@core/Areas/RaceCard/constants'
import {
  BlackbookButtons,
  BonusBet,
  ReBetButton,
  TicketInvestment,
  Specials,
  SelectionInfo,
} from './Components'

interface Params extends Record<string, string> {
  tab: Tabs
}

const enum LocalConstants {
  PreviewModeClass = 'js-details--preview',
  ShortcutSectionClass = 'js-details__shortcuts',
}

export const Details: React.FC<{ ticket: Ticket }> = ({ ticket }) => {
  const { tab: activeTab } = useParams<Params>() as Params
  const ticketDisplayContext = React.useContext(TicketDisplayContext)

  const isContextSharePreview = ticketDisplayContext.type === 'share'
  const shouldHideCost = isContextSharePreview && ticketDisplayContext.data.shouldHideCost

  const selection = ticket.legs[0].selections[0]

  if (!selection || selection.selectionDetails.length === 0) return null

  const ticketLayoutType = getTicketLayoutType(ticket)
  const isFavouriteNumbers = ticket.planSeq === PlanSeq.FavouriteNumbers
  const isSameRaceMulti = ticketLayoutType === 'same-race-multi'

  const shouldHideStartTime =
    isFavouriteNumbers || isTippingTicket(ticket) || (ticket.legs.length > 1 && !isSameRaceMulti)
  const shouldDisplayNewStartTime: boolean =
    activeTab === 'pending' &&
    !!ticket.updatedEventStartDateTime &&
    ticket.eventStartDateTime !== ticket.updatedEventStartDateTime

  return (
    <DetailsStyled
      className={isContextSharePreview ? LocalConstants.PreviewModeClass : ''}
      data-testid='ticket-detail'
    >
      {!isContextSharePreview && (
        <section className={LocalConstants.ShortcutSectionClass}>
          <ReBetButton ticket={ticket} activeTab={activeTab} />

          <BlackbookButtons ticket={ticket} activeTab={activeTab} />
        </section>
      )}
      
      <SelectionInfo ticket={ticket} />

      {ticket.specialInformation && (
        <Specials specials={ticket.specialInformation} shouldHideCost={shouldHideCost} />
      )}

      {!shouldHideCost && (
        <>
          <TicketInvestment ticket={ticket} />
          <BonusBet investment={ticket.investment} />
        </>
      )}

      {!isContextSharePreview && (
        <SecondaryInfo data-testid='ticket-detail-secondary-info'>
          <dl>
            {!shouldHideStartTime && (
              <div data-testid='ticket-detail-start-time'>
                <dt>Event Start:</dt>
                <dd>
                  {formatDateTime(ticket.updatedEventStartDateTime)}

                  {shouldDisplayNewStartTime && (
                    <strong style={{ color: hexColors.summerSky }}> New</strong>
                  )}
                </dd>
              </div>
            )}

            <div data-testid='ticket-detail-bet-placed'>
              <dt>Bet Placed on:</dt>
              <dd>{formatDateTime(ticket.timestamp, true)}</dd>
            </div>

            <div data-testid='ticket-detail-ticket-id'>
              <dt>Bet ID:</dt>
              <dd>{ticket.ticketSequence}</dd>
            </div>
          </dl>
        </SecondaryInfo>
      )}

      {!isContextSharePreview && ticket.bettingInformation && !isSameRaceMulti && (
        <SecondaryInfo>
          <dl>
            {getBettingInformationWithOverrides(ticket)?.map(b => (
              <span data-testid='ticket-detail-betting-information' key={b.title}>
                <dt>{b.title}:</dt>
                <dd>{b.detail.join('. ')}</dd>
              </span>
            ))}

            {ticket.specialInformation?.map(s => (
              <span data-testid='ticket-detail-special-info' key={s.title}>
                <dt>{s.title}:</dt>
                <dd>{s.description}</dd>
              </span>
            ))}
          </dl>
        </SecondaryInfo>
      )}
    </DetailsStyled>
  )
}

// =============
// Local Helpers
// =============

function getBettingInformationWithOverrides(ticket: Ticket) {
  if (ticket.productName === FixedOddsMarketDisplayNames.SP) {
    return BettingInformationOverrides.StartingPrice
  } else {
    return ticket.bettingInformation
  }
}

// ======
// Styles
// ======

const DetailsStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.sm,
  paddingTop: spacing.smx1,

  ':empty': { display: 'none' },

  '> div:empty': { display: 'none' },

  [`&.${LocalConstants.ShortcutSectionClass}`]: {
    ':empty': { display: 'none' },

    '> div': {
      paddingTop: spacing.smx1,
      paddingBottom: spacing.smx1,
      borderTop: `0.5px solid ${colors.neutral[200]}`,
    },
  },

  table: {
    fontSize: '1.2rem',
    color: hexColors.nero,
    width: '100%',
    padding: 0,
    border: 0,
    borderSpacing: 0,
  },

  [`&.${LocalConstants.PreviewModeClass}`]: {
    paddingTop: 0,
  },
})
